import { useEffect, useMemo } from 'react';
import { addressesAdapter } from '../libs/addressesAdapter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addressesAPI } from '../api/address';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import {
  setAddresses,
  setSelectedAddress,
} from '../../../store/slices/userAddresses';
import { setLocation } from '../../../store/slices/locationSlice';
import { captureApiError } from '../../../lib/sentry-utils';
import {
  getGuestAddressPayload,
  guestAddressFromPayload,
} from '../libs/addressStorage';
export const useGetAddresses = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const address = localStorage.getItem('address') || '';
  const hasToken = !!localStorage.getItem('token');

  const initData = useMemo(() => ({ addresses: addressesAdapter() }), []);

  const { data = initData, isLoading } = useQuery({
    queryKey: ['addresses'],
    queryFn: async () => {
      try {
        return await addressesAPI.getAddresses();
      } catch (error) {
        console.error(
          'GetAddresses error:',
          JSON.stringify(error, Object.getOwnPropertyNames(error), 2)
        );
        captureApiError(error, '/api/addresses', 'GET');
        throw error;
      }
    },
    select: data => ({
      addresses: addressesAdapter(get(data, 'data.addresses')),
    }),
    enabled: hasToken,
  });

  // Guests have no backend address list to fetch — fall back to whatever
  // address they saved locally (see useCreateAddress) so every consumer of
  // this hook sees it just like a real one.
  const guestAddresses = useMemo(() => {
    if (hasToken) return [];

    const guestPayload = getGuestAddressPayload();
    return guestPayload ? [guestAddressFromPayload(guestPayload)] : [];
  }, [hasToken]);

  useEffect(() => {
    if (hasToken || !guestAddresses.length) return;

    const [guestAddress] = guestAddresses;
    dispatch(setAddresses(guestAddresses));
    dispatch(setSelectedAddress(guestAddress._id));
    dispatch(
      setLocation({
        latitude: guestAddress.location.lat,
        longitude: guestAddress.location.lon,
      })
    );
  }, [dispatch, hasToken, guestAddresses]);

  // ✅ Save to redux once data is available
  useEffect(() => {
    if (data?.addresses?.length) {
      dispatch(setAddresses(data.addresses));

      // If user has a saved address in localStorage, set location to that address coordinates
      if (address) {
        const savedAddress = data.addresses.find(
          addr => String(addr._id) === String(address)
        );

        if (savedAddress) {
          // Force update location coordinates
          dispatch(
            setLocation({
              latitude: savedAddress.location.lat,
              longitude: savedAddress.location.lon,
            })
          );

          // Force refresh nearest branch and delivery details with new coordinates
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['nearest-branch'] });
            queryClient.invalidateQueries({ queryKey: ['delivery-detail'] });
          }, 100);
        } else {
        }
      }
    }
  }, [data?.addresses, dispatch, address]);

  return {
    ...data,
    addresses: hasToken ? data.addresses : guestAddresses,
    isLoading,
  };
};
